import React, { useState, useEffect, useRef, useCallback } from "react";
import { Constants } from "../../../constants/appConstants";
import { SummaryRequest } from "../../../models/csos/summaryRequest";
import { SummaryResponse } from "../../../models/csos/summaryResponse";
import { Order } from "../../../models/csos/order";
import { CustomerOrder } from "../../../models/utility/customOrderElement";
import { OrderColumnWidths } from "../../../models/csos/orderColumnWidths";
import { csosApiService } from "../../../services/api/csosApiService";
import { dateService } from "../../../services/dateService";
import { useOrderDetailsService } from "../../../services/component/orderDetailsService";
import { userService } from "../../../services/userService";
import LoadingContainer from "../../loadingContainer/LoadingContainer";
import SignApproveOrderLayer from "../../signApproveOrdersLayer/table/SignApproveOrderLayer";
import "./PendingTable.css";

const initialCustomerOrder: CustomerOrder[] = [
  { className: "", title: "Unique Tracking Number", id: "uniqueTrackingNum", value: "UTN" },
  { className: "", title: "Time orders must be processed by to ensure next delivery schedule", id: "cutOffTime", value: "DC CUTOFF" },
  { className: "", title: "Current approval status", id: "status", value: "STATUS" },
  { className: "", title: "DEA Registration Number", id: "deaLicenseId", value: "DEA #" },
  { className: "", title: "Account Name", id: "customerName", value: "ACCOUNT NAME" },
  { className: "", title: "Purchase Order Number", id: "purchaseOrderId", value: "PO #" },
  { className: "", title: "Order Submission Method", id: "orderMethod", value: "ORDER METHOD" },
  { className: "", title: "Customer Number", id: "account", value: "DC-ACCOUNT" },
  { className: "", title: "Order Number", id: "orderId", value: "ORDER #" },
  { className: "", title: "Time Order Submitted", id: "submitted", value: "ORDER DATE/TIME" },
  { className: "", title: "Order updated by User", id: "lastUpdatedUser", value: "LAST UPDATED BY" },
  { className: "", title: "Time Order was updated", id: "lastUpdatedTime", value: "LAST UPDATED TIME" },
];

const PendingTable: React.FC = () => {
  const orderDetailsService = useOrderDetailsService();
  const [response, setResponse] = useState<SummaryResponse>(new SummaryResponse("0", "0", 0, []));
  const [loading, setLoading] = useState(false);
  const [pageText, setPageText] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [backward, setBackward] = useState(false);
  const [forward, setForward] = useState(true);
  const [refreshEnabled, setRefreshEnabled] = useState(true);
  const [displayRefresh, setDisplayRefresh] = useState("active-refresh");
  const [colWidths, setColWidths] = useState<OrderColumnWidths>(new OrderColumnWidths());
  const [customerOrder, setCustomerOrder] = useState<CustomerOrder[]>(
    initialCustomerOrder.map((co) => ({ ...co }))
  );
  const [headerOffset, setHeaderOffset] = useState("0px");
  const [sortfield, setSortfield] = useState("");
  const [sortType, setSortType] = useState("");
  const pageInputRef = useRef<HTMLInputElement>(null);
  const limit = Constants.LIMIT;
  const pageStart = Constants.START_PAGE;
  const onInitFlagRef = useRef(true);
  const refreshIntervalsRef = useRef<number[]>([]);

  const getOrderData = useCallback(
    (sType: string, status: string, sField: string, lim: number, offset: number) => {
      const summaryRequest = new SummaryRequest();
      summaryRequest.sort = sType + sField;
      summaryRequest.status = status;
      summaryRequest.limit = lim;
      summaryRequest.offset = offset;
      csosApiService.fetchtOrdersData(summaryRequest).then((res) => {
        setLoading(false);
        if (res.body) {
          const r = res.body as SummaryResponse;
          const normalizedOrders = r.orders ?? [];
          setResponse({ ...r, orders: normalizedOrders });
          const totalRecords = Number(r.totalNumberOfRecords ?? 0);
          orderDetailsService.TotalPendingOrder(totalRecords);
          let tp: number;
          if (totalRecords % lim === 0) {
            tp = totalRecords / lim;
          } else {
            tp = Math.floor(totalRecords / lim) + 1;
          }
          setTotalPage(tp);
          if (onInitFlagRef.current) {
            onInitFlagRef.current = false;
            if (tp === pageStart) setForward(false);
          }
          calcColWidths(normalizedOrders);
        }
      }).catch(() => {
        setLoading(false);
      });
    },
    [orderDetailsService]
  );

  useEffect(() => {
    setRefreshEnabled(false);
    onInitFlagRef.current = true;
    setBackward(false);
    setForward(true);
    setPageText(pageStart);
    setDisplayRefresh("active-refresh");
    setLoading(false);
    getOrderData("", "pending", "", limit, pageStart);
    return () => {
      clearAllPendingIntervals();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const unsub1 = orderDetailsService.refreshOrder$.subscribe((refresh) => {
      if (refresh) {
        setRefreshEnabled(false);
        doRefresh("");
      }
    });
    const unsub2 = orderDetailsService.openSignApprove$.subscribe((openSignLayer) => {
      if (openSignLayer) {
        setSortfield("");
        setSortType("");
        if (pageInputRef.current) pageInputRef.current.value = "1";
        getOrderData("", "pending", "", limit, pageStart);
      }
    });
    return () => { unsub1(); unsub2(); };
    // eslint-disable-next-line
  }, [orderDetailsService]);

  const calcColWidths = (orders: Order[] = []) => {
    const widths = new OrderColumnWidths();
    for (const order of orders) {
      for (const key in widths) {
        const val = (order as any)[key];
        if (val && val.length > widths[key] && val.length > key.length) {
          widths[key] = val.length;
        }
      }
    }
    setColWidths(widths);
  };

  const validateButton = (page: number, tp: number) => {
    setBackward(+page !== 1);
    setForward(+page !== +tp);
  };

  const nextPage = () => {
    const newPage = pageText + 1;
    setPageText(newPage);
    validateButton(newPage, totalPage);
    const offset = limit * (newPage - 1) + 1;
    getOrderData(sortType, "pending", sortfield, limit, offset);
  };

  const prevPage = () => {
    const newPage = pageText - 1;
    setPageText(newPage);
    validateButton(newPage, totalPage);
    const offset = limit * (newPage - 1) + 1;
    getOrderData(sortType, "pending", sortfield, limit, offset);
  };

  const assignPage = (value: string) => {
    const page = parseInt(value, 10);
    if (page <= totalPage && page > 0 && !isNaN(page)) {
      setPageText(page);
      const offset = limit * (page - 1) + 1;
      getOrderData(sortType, "pending", sortfield, limit, offset);
    }
    validateButton(page, totalPage);
  };

  const sortTable = (pendingData: CustomerOrder, idx: number) => {
    const currentClass = pendingData.className;
    const updated = customerOrder.map((co) => ({ ...co, className: "" }));
    let newSortType = "";
    let newSortField = "";
    if (currentClass === "") {
      updated[idx].className = "glyphicon glyphicon-chevron-up";
      newSortType = "%2B";
      newSortField = pendingData.id;
    } else if (currentClass === "glyphicon glyphicon-chevron-up") {
      updated[idx].className = "glyphicon glyphicon-chevron-down";
      newSortType = "-";
      newSortField = pendingData.id;
    }
    setCustomerOrder(updated);
    setSortType(newSortType);
    setSortfield(newSortField);
    const offset = limit * (pageText - 1) + 1;
    getOrderData(newSortType, "pending", newSortField, limit, offset);
  };

  const clearAllPendingIntervals = () => {
    refreshIntervalsRef.current.forEach((id) => clearInterval(id));
    refreshIntervalsRef.current = [];
  };

  const doRefresh = (fromPage: string) => {
    if (!refreshEnabled) {
      setLoading(true);
      setSortfield("");
      setSortType("");
      getOrderData("", "pending", "", limit, pageStart);
      if (fromPage === Constants.FROM_PENDING_PAGE) {
        setRefreshEnabled(true);
        setDisplayRefresh("inactive-refresh");
        const timer = window.setInterval(() => {
          setRefreshEnabled(false);
          setDisplayRefresh("active-refresh");
          clearAllPendingIntervals();
        }, 30000);
        refreshIntervalsRef.current.push(timer);
      }
      validateButton(pageStart, totalPage);
      if (pageInputRef.current) pageInputRef.current.value = "1";
    }
  };

  const checkNoOfApprovedOrders = (): boolean => {
    return response != null && Number(response.totalNumOfApprovedOrders ?? 0) > 0;
  };

  const checkPagination = (): boolean => {
    return response != null && Number(response.totalNumberOfRecords ?? 0) > 0;
  };

  const onScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const left = (event.target as HTMLDivElement).scrollLeft;
    setHeaderOffset(left > 0 ? `-${left}px` : "0px");
  };

  const handleOrderClick = (order: Order) => {
    orderDetailsService.announceOrder(order);
  };

  const getColStyle = (minWidth: number) => {
    if (minWidth !== 0) return { minWidth: `${30 + minWidth * 7}px` };
    return { minWidth: `${window.screen.width / 12.1}px` };
  };

  return (
    <>
      <div className="pending-section">
        <div>
          <span>
            <button
              disabled={!checkNoOfApprovedOrders()}
              className={`btn active-button ${!checkNoOfApprovedOrders() ? "signmyApproveOrderDisabled" : ""}`}
              onClick={() => orderDetailsService.openSignApprove("openSignLayer")}
            >
              Sign My Approved Orders
            </button>
          </span>
        </div>
      </div>
      <SignApproveOrderLayer onSignEmit={() => doRefresh("")} />
      <LoadingContainer loading={loading} />

      <div className="pagination-section">
        <div onClick={() => doRefresh(Constants.FROM_PENDING_PAGE)}>
          <span className={`refresh ${displayRefresh}`}>
            {!refreshEnabled && <img src="/assets/images/refreshb.png" alt="refresh" />}
            {refreshEnabled && <img className="valid-image" src="/assets/images/refresh.gif" alt="refresh" />}
            {Constants.REFRESH}
          </span>
        </div>
        {checkPagination() && (
          <span className="pagination-header">
            <span>{Constants.PAGE}</span>
            <input
              ref={pageInputRef}
              type="text"
              defaultValue="1"
              className="pagination-text"
              onKeyUp={(e) => { if (e.key === "Enter") assignPage((e.target as HTMLInputElement).value); }}
              onChange={(e) => { assignPage(e.target.value); }}
            />
            <span> {Constants.OF} {totalPage}</span>
            {backward && (
              <span className="pagination-button" onClick={prevPage}>
                <img className="valid-image" src="/assets/images/icon-arrow-left.png" alt="prev" />
              </span>
            )}
            {forward && (
              <span className="pagination-button" onClick={nextPage}>
                <img className="valid-image" src="/assets/images/icon-arrow-right.png" alt="next" />
              </span>
            )}
          </span>
        )}
      </div>

      <div className="pending-table-container" onScroll={onScroll}>
        <table className="table table-bordered">
          <thead>
            <tr style={{ left: headerOffset }}>
              {customerOrder.map((pendingData, i) => (
                <th
                  key={pendingData.id}
                  style={getColStyle((colWidths as any)[pendingData.id] || 0)}
                  onClick={() => sortTable(pendingData, i)}
                >
                  <span className="text-nowrap" title={pendingData.title}>
                    {pendingData.value}
                    {(pendingData.value === "DC CUTOFF" || pendingData.value === "LAST UPDATED TIME" || pendingData.value === "ORDER DATE/TIME") && (
                      <><br />({dateService.getTimeZoneAbbr()})</>
                    )}
                  </span>
                  <span className={pendingData.className}></span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(response.orders ?? []).map((order, idx) => (
              <tr key={idx} className={order.statusDesc === "Failed" ? "order-fail" : ""}>
                <td className="pending-row-td text-nowrap text-center" style={getColStyle(colWidths.uniqueTrackingNum)}>
                  <a onClick={() => handleOrderClick(order)}>{order.uniqueTrackingNum}</a>
                </td>
                <td className="pending-row-td text-nowrap text-center" style={getColStyle(colWidths.cutOffTime)}>
                  {dateService.localizeDate(order.cutOffTime, "MM/DD/YYYY hh:mm A", "ddd hh:mm A")}
                  {order.cutOffTimeEndFlag && (order.statusCode === "10" || order.statusCode === "11" || order.statusCode === "13") && (
                    <span className="exclamation-mark"><img src="/assets/images/alert.png" alt="alert" /></span>
                  )}
                </td>
                <td className="pending-row-td text-nowrap text-center" style={getColStyle(colWidths.status)}>
                  {order.orderLocked ? "Locked" : order.statusDesc}
                </td>
                <td className="pending-row-td text-nowrap text-center" style={getColStyle(colWidths.deaLicenseId)}>{order.deaLicenseId}</td>
                <td className="pending-row-td text-nowrap text-center" style={getColStyle(colWidths.customerName)}>{order.customerName}</td>
                <td className="pending-row-td text-nowrap text-left" style={getColStyle(colWidths.purchaseOrderId)}>{order.purchaseOrderId}</td>
                <td className="pending-row-td text-nowrap text-center" style={getColStyle(colWidths.orderMethod)}>{order.orderMethod}</td>
                <td className="pending-row-td text-nowrap text-center" style={getColStyle(colWidths.account)}>{order.account}</td>
                <td className="pending-row-td text-nowrap text-center" style={getColStyle(colWidths.orderId)}>
                  <a onClick={() => handleOrderClick(order)}>{order.orderId}</a>
                </td>
                <td className="pending-row-td text-nowrap text-center" style={getColStyle(colWidths.submitted)}>
                  {dateService.localizeDate(order.submitted, "MM/DD/YYYY hh:mm A", "MM/DD/YYYY hh:mm A")}
                </td>
                <td className="pending-row-td text-nowrap text-center" style={getColStyle(colWidths.lastUpdatedUser)}>{order.lastUpdatedUser}</td>
                <td className="pending-row-td text-nowrap text-center" style={getColStyle(colWidths.lastUpdatedTime)}>
                  {dateService.localizeDate(order.lastUpdatedTime, "MM/DD/YYYY hh:mm A", "MM/DD/YYYY hh:mm A")}
                </td>
              </tr>
            ))}
            {+response.totalNumberOfRecords === 0 && !onInitFlagRef.current && (
              <tr><td colSpan={12}>No Orders to approve at this time</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default PendingTable;
